import { Trans, useLingui } from '@lingui/react/macro';
import type { FC } from 'react';
import { useParams } from 'wouter';
import { useConvertPreview } from '@/account/trade/convert/api';
import { Breadcrumb } from '@/base/Breadcrumb';
import { SEO_ORIGIN } from '@/base/constants';
import { PageMeta } from '@/base/PageMeta';
import { Link } from '@/base/router';
import { useSettings } from '@/settings/api';
import { useCoin } from '@/shared/api';
import { Button } from '@/shared/Button';
import { Coin } from '@/shared/Coin';
import { Digits } from '@/shared/Digits';
import { usePupeteerSignal } from '@/shared/usePupeteerSignal';
import { LandingLayout } from './LandingLayout';
import { UnitConverter } from './UnitConverter';

export const ConverterPage: FC = () => {
  const { t } = useLingui();
  const [{ language }] = useSettings();
  const { from, to } = useParams<{ from: string; to: string }>();
  const baseSlug = from?.toUpperCase();
  const quoteSlug = to?.toUpperCase();
  const fromCoin = useCoin(baseSlug);
  const toCoin = useCoin(quoteSlug);
  const rate = useConvertPreview({
    from_coin: baseSlug,
    to_coin: quoteSlug,
    from_amount: '1',
  });
  usePupeteerSignal(!!fromCoin.data && !!toCoin.data);

  const isValid =
    (fromCoin.isLoading || !!fromCoin.data) &&
    (toCoin.isLoading || !!toCoin.data);

  if (!isValid) {
    throw new Error(
      `404:${t`We couldn't load a converter for ${baseSlug} to ${quoteSlug}. It may be temporarily unavailable or the pair is incorrect.`}`,
    );
  }

  // The API's coin name for IQD is just "Dinar" — say "Iraqi Dinar" instead so
  // the page actually matches searches like "bitcoin price iraq".
  const baseName =
    baseSlug === 'IQD' ? t`Iraqi Dinar` : (fromCoin.data?.name ?? baseSlug);
  const quoteName =
    quoteSlug === 'IQD' ? t`Iraqi Dinar` : (toCoin.data?.name ?? quoteSlug);

  const home = `${SEO_ORIGIN}/${language}`;

  return (
    <LandingLayout>
      {fromCoin.data && toCoin.data && (
        <PageMeta
          title={t`${baseName} to ${quoteName} Converter`}
          description={t`Convert ${baseName} (${baseSlug}) to ${quoteName} (${quoteSlug}) instantly with live exchange rates on IQX — Iraq's first crypto exchange.`}
          localizedPath={`/converter/${from}/${to}`}
        />
      )}
      <Breadcrumb
        items={[
          { name: t`Home`, url: home },
          { name: t`Converter`, url: `${home}/converter` },
          {
            name: `${baseSlug}/${quoteSlug}`,
            url: `${home}/converter/${from}/${to}`,
          },
        ]}
        className="desktop:mb-12 mb-6"
      />
      <section className="max-w-desktop mx-auto w-full px-3 py-16 text-center desktop:min-h-[70svh] flex flex-col items-center desktop:gap-4">
        <div className="flex items-center justify-center gap-3 flex-wrap mb-3">
          <Coin coin={fromCoin.data} size={12} variant="icon" priority />
          <h1 className="text-3xl desktop:text-5xl font-normal leading-tight text-content-primary">
            <Trans>
              Convert <Coin coin={baseSlug} size={10} variant="name" />{' '}
              <Coin
                coin={baseSlug}
                size={10}
                variant="symbol"
                colored
                className="font-semibold"
              />{' '}
              to <Coin coin={quoteSlug} size={10} variant="name" />{' '}
              <Coin
                coin={quoteSlug}
                size={10}
                variant="symbol"
                colored
                className="font-semibold"
              />
            </Trans>
          </h1>
        </div>

        <div className="inline-flex items-center gap-2 text-content-tertiary text-sm desktop:text-lg mb-10">
          <Digits
            value="1"
            coin={baseSlug}
            className={rate.isLoading ? 'shimmer' : 'text-content-primary'}
          />
          <span>=</span>
          <Digits
            value={rate.data?.to_amount}
            coin={quoteSlug}
            className={rate.isLoading ? 'shimmer' : 'text-content-primary'}
          />
        </div>

        <UnitConverter
          key={`${baseSlug}-${quoteSlug}`}
          fromCoin={baseSlug}
          toCoin={quoteSlug}
          className="max-w-5xl mx-auto"
        />

        <Link
          to={`/${language}/buy-and-sell/${baseSlug}`}
          className="inline-block mt-6"
        >
          <Button size={12} variant="brand" block className="px-8">
            <Trans>
              Buy <Coin coin={baseSlug} size={10} variant="name" />
            </Trans>
          </Button>
        </Link>

        <p className="text-content-tertiary text-sm max-w-xl mx-auto mt-10">
          <Trans>
            Live <Coin coin={baseSlug} size={10} variant="name" /> to{' '}
            <Coin coin={quoteSlug} size={10} variant="name" /> exchange rate,
            updated in real time on IQX.
          </Trans>
        </p>
      </section>
    </LandingLayout>
  );
};
